import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '@/services/api/apiClient';
import { ArrowLeft, Download, CreditCard, Calendar, TrendingUp, Package, ShoppingCart, AlertCircle } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

interface Purchase {
  purchase_id: number;
  product_key: string;
  credits_purchased: number;
  amount_paid: number;
  purchased_at: string;
  applied_to_year: number;
  notes?: string;
  payment_intent?: string;
  payment_status?: string;
}

interface CurrentUsage {
  year: number;
  images_generated: number;
  images_limit: number;
  remaining: number;
}

interface Summary {
  total_purchases: number;
  total_credits_purchased: number;
  total_amount_paid: number;
  first_purchase?: string;
  last_purchase?: string;
}

interface PurchaseHistoryData {
  purchases: Purchase[];
  current_usage: CurrentUsage;
  summary: Summary;
}

const AIImagePurchaseHistory: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<PurchaseHistoryData | null>(null);
  const [yearlyData, setYearlyData] = useState<any[]>([]);
  const [processingCheckout, setProcessingCheckout] = useState(false);

  useEffect(() => {
    fetchPurchaseHistory();
    fetchYearlySummary();
  }, []);

  const fetchPurchaseHistory = async () => {
    try {
      const response = await apiClient.get('/purchases/ai-images');
      if (response.data.success) {
        setData(response.data);
      }
    } catch (error) {
      console.error('Error fetching purchase history:', error);
      toast.error('Failed to load purchase history');
    } finally {
      setLoading(false);
    }
  };

  const fetchYearlySummary = async () => {
    try {
      const response = await apiClient.get('/purchases/ai-images/yearly-summary');
      if (response.data.success) {
        setYearlyData(response.data.yearly_summary);
      }
    } catch (error) {
      console.error('Error fetching yearly summary:', error);
    }
  };

  const handlePurchaseMoreCredits = async () => {
    setProcessingCheckout(true);
    try {
      console.log('🔄 Initiating AI Image credits purchase...');
      
      const response = await apiClient.post('/stripe/ai-addon-checkout', {
        product_key: 'ai_images_100'
      });
      
      const data = response.data || response;
      
      if (data.success && data.checkout_url) {
        console.log('🔗 Redirecting to Stripe:', data.checkout_url);
        window.location.href = data.checkout_url;
      } else if (data.checkout_url) {
        console.log('🔗 Redirecting to Stripe (no success flag):', data.checkout_url);
        window.location.href = data.checkout_url;
      } else {
        toast.error('Failed to create checkout session. Please try again.');
      }
    } catch (error: any) {
      console.error('❌ Purchase error:', error);
      
      if (error.response?.status === 401) {
        window.location.href = '/login';
      } else {
        toast.error(`Failed to process purchase: ${error.response?.data?.detail || error.message || 'Unknown error'}`);
      }
    } finally {
      setProcessingCheckout(false);
    }
  };

  const downloadCSV = () => {
    if (!data?.purchases.length) {
      toast.error('No purchases to export');
      return;
    }

    try {
      const headers = ['Date', 'Time', 'Credits', 'Amount', 'Year Applied', 'Status', 'Transaction ID'];
      const rows = data.purchases.map(p => [
        format(new Date(p.purchased_at), 'yyyy-MM-dd'),
        format(new Date(p.purchased_at), 'HH:mm:ss'),
        p.credits_purchased.toString(),
        `${p.amount_paid.toFixed(2)}`,
        p.applied_to_year.toString(),
        p.payment_status || 'completed',
        p.payment_intent ? p.payment_intent.slice(-8) : ''
      ]);

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ai-image-purchases-${format(new Date(), 'yyyy-MM-dd')}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success(`Exported ${data.purchases.length} purchase${data.purchases.length > 1 ? 's' : ''} to CSV`);
    } catch (error) {
      console.error('Error downloading CSV:', error);
      toast.error('Failed to export purchase history');
    }
  };

  const getStatusBadge = (status?: string) => {
    const statusMap: Record<string, { className: string; label: string }> = {
      completed: { className: 'bg-yellow-400 text-black hover:bg-yellow-500', label: 'Completed' },
      pending: { className: 'bg-gray-400 text-white hover:bg-gray-500', label: 'Pending' },
      failed: { className: 'bg-red-500 text-white hover:bg-red-600', label: 'Failed' },
      refunded: { className: 'bg-gray-500 text-white hover:bg-gray-600', label: 'Refunded' }
    };
    
    const config = statusMap[status || 'completed'];
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#1f4ed1] to-[#2256e5] py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header Skeleton */}
          <div className="mb-8">
            <div className="h-8 w-32 bg-white/20 rounded animate-pulse mb-4"></div>
            <div className="h-8 w-64 bg-white/20 rounded animate-pulse mb-2"></div>
            <div className="h-4 w-96 bg-white/20 rounded animate-pulse"></div>
          </div>

          {/* Current Usage Card Skeleton */}
          <Card className="mb-8 bg-white/10 backdrop-blur-sm border-white/20">
            <CardHeader className="pb-3">
              <div className="h-6 w-48 bg-white/20 rounded animate-pulse"></div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex justify-between">
                  <div className="h-4 w-24 bg-white/20 rounded animate-pulse"></div>
                  <div className="h-4 w-16 bg-white/20 rounded animate-pulse"></div>
                </div>
                <div className="h-2 w-full bg-white/20 rounded animate-pulse"></div>
                <div className="flex justify-between">
                  <div className="h-4 w-32 bg-white/20 rounded animate-pulse"></div>
                  <div className="h-4 w-12 bg-white/20 rounded animate-pulse"></div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Summary Stats Skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            {[1, 2, 3, 4].map((i) => (
              <Card key={i} className="bg-white/10 backdrop-blur-sm border-white/20">
                <CardHeader className="pb-2">
                  <div className="h-4 w-24 bg-white/20 rounded animate-pulse"></div>
                </CardHeader>
                <CardContent>
                  <div className="h-8 w-16 bg-white/20 rounded animate-pulse"></div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Purchase History Table Skeleton */}
          <Card className="bg-white/10 backdrop-blur-sm border-white/20">
            <CardHeader>
              <div className="h-6 w-40 bg-white/20 rounded animate-pulse"></div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex space-x-4 py-3 border-b border-white/10">
                    <div className="h-4 w-24 bg-white/20 rounded animate-pulse"></div>
                    <div className="h-4 w-32 bg-white/20 rounded animate-pulse"></div>
                    <div className="h-4 w-16 bg-white/20 rounded animate-pulse"></div>
                    <div className="h-4 w-20 bg-white/20 rounded animate-pulse"></div>
                    <div className="h-4 w-16 bg-white/20 rounded animate-pulse"></div>
                    <div className="h-4 w-20 bg-white/20 rounded animate-pulse"></div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const usagePercentage = data?.current_usage.images_limit 
    ? (data.current_usage.images_generated / data.current_usage.images_limit) * 100
    : 0;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1f4ed1] to-[#2256e5] py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/restaurant-dashboard')}
            className="mb-4 text-white hover:text-yellow-400 hover:bg-yellow-400/10 transition-all duration-200 group"
          >
            <ArrowLeft className="mr-2 h-4 w-4 group-hover:text-yellow-400" />
            Back to Dashboard
          </Button>
          
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-white">AI Image Purchase History</h1>
              <p className="mt-2 text-white/80">Track your AI image credit purchases and usage</p>
            </div>
            
            <div className="flex gap-2">
              <Button 
                onClick={handlePurchaseMoreCredits}
                disabled={processingCheckout}
                variant="default"
                className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 disabled:opacity-50"
              >
                <ShoppingCart className="mr-2 h-4 w-4" />
                {processingCheckout ? 'Processing...' : 'Purchase More Credits'}
              </Button>
              {data?.purchases.length ? (
                <Button onClick={downloadCSV} variant="outline" className="bg-white/10 border-white/20 text-white hover:bg-white/20">
                  <Download className="mr-2 h-4 w-4" />
                  Export CSV
                </Button>
              ) : null}
            </div>
          </div>
        </div>

        {/* Low Credit Warning */}
        {data?.current_usage && usagePercentage > 80 && (
          <div className="mb-6 bg-yellow-500/20 border border-yellow-400/30 rounded-lg p-4 flex items-center justify-between">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-yellow-400 mr-3" />
              <div>
                <p className="font-medium text-white">Running Low on Credits!</p>
                <p className="text-sm text-white/70">
                  You have {data.current_usage.remaining} credits remaining ({Math.round(100 - usagePercentage)}% left)
                </p>
              </div>
            </div>
            <Button 
              onClick={handlePurchaseMoreCredits}
              disabled={processingCheckout}
              size="sm"
              className="bg-yellow-500 hover:bg-yellow-600 text-black disabled:opacity-50"
            >
              <ShoppingCart className="mr-2 h-4 w-4" />
              {processingCheckout ? 'Processing...' : 'Purchase More'}
            </Button>
          </div>
        )}

        {/* Current Usage Card */}
        {data?.current_usage && (
          <Card className="mb-8 bg-white/10 backdrop-blur-sm border-white/20">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center text-white">
                <TrendingUp className="mr-2 h-5 w-5 text-blue-400" />
                Current Year Usage ({data.current_usage.year})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex justify-between text-sm">
                  <span className="text-white/70">Credits Used</span>
                  <span className="font-medium text-white">
                    {data.current_usage.images_generated} / {data.current_usage.images_limit}
                  </span>
                </div>
                <Progress value={usagePercentage} className="h-2 [&>div]:bg-yellow-400" />
                <div className="flex justify-between text-sm">
                  <span className="text-white/70">Remaining Credits</span>
                  <span className="font-medium text-green-400">
                    {data.current_usage.remaining}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Summary Stats */}
        {data?.summary && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <Card className="bg-white/10 backdrop-blur-sm border-white/20">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-white/70">
                  Total Purchases
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold text-white">{data.summary.total_purchases}</p>
              </CardContent>
            </Card>
            
            <Card className="bg-white/10 backdrop-blur-sm border-white/20">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-white/70">
                  Total Credits
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold text-white">
                  {data.summary.total_credits_purchased.toLocaleString()}
                </p>
              </CardContent>
            </Card>
            
            <Card className="bg-white/10 backdrop-blur-sm border-white/20">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-white/70">
                  Total Spent
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold text-white">
                  ${data.summary.total_amount_paid.toFixed(2)}
                </p>
              </CardContent>
            </Card>
            
            <Card className="bg-white/10 backdrop-blur-sm border-white/20">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-white/70">
                  Last Purchase
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold text-white">
                  {data.summary.last_purchase 
                    ? format(new Date(data.summary.last_purchase), 'MMM d, yyyy')
                    : 'Never'}
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Purchase History Table */}
        <Card className="bg-white/10 backdrop-blur-sm border-white/20">
          <CardHeader>
            <CardTitle className="flex items-center text-white">
              <CreditCard className="mr-2 h-5 w-5 text-blue-400" />
              Purchase History
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data?.purchases.length ? (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10">
                      <TableHead className="text-white/70">Date</TableHead>
                      <TableHead className="text-white/70">Product</TableHead>
                      <TableHead className="text-right text-white/70">Credits</TableHead>
                      <TableHead className="text-right text-white/70">Amount</TableHead>
                      <TableHead className="text-white/70">Applied Year</TableHead>
                      <TableHead className="text-white/70">Status</TableHead>
                      <TableHead className="text-white/70">Transaction ID</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.purchases.map((purchase) => (
                      <TableRow key={purchase.purchase_id} className="border-white/10">
                        <TableCell className="text-white">
                          <div className="flex items-center">
                            <Calendar className="mr-2 h-4 w-4 text-white/40" />
                            {format(new Date(purchase.purchased_at), 'MMM d, yyyy')}
                            <span className="ml-2 text-white/50 text-xs">
                              {format(new Date(purchase.purchased_at), 'HH:mm')}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="text-white">
                          <div className="flex items-center">
                            <Package className="mr-2 h-4 w-4 text-white/40" />
                            <span className="font-medium">AI Image Credits</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-medium text-green-400">
                          +{purchase.credits_purchased}
                        </TableCell>
                        <TableCell className="text-right font-medium text-white">
                          ${purchase.amount_paid.toFixed(2)}
                        </TableCell>
                        <TableCell className="text-white">
                          <Badge className="bg-yellow-400 text-black hover:bg-yellow-500">{purchase.applied_to_year}</Badge>
                        </TableCell>
                        <TableCell>
                          {getStatusBadge(purchase.payment_status)}
                        </TableCell>
                        <TableCell className="text-white">
                          {purchase.payment_intent && (
                            <code className="text-xs bg-white/10 text-white/70 px-2 py-1 rounded">
                              {purchase.payment_intent.slice(-8)}
                            </code>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="text-center py-12">
                <CreditCard className="mx-auto h-12 w-12 text-white/40 mb-4" />
                <h3 className="text-lg font-medium text-white mb-2">
                  No purchases yet
                </h3>
                <p className="text-white/70 mb-6">
                  Purchase AI image credits to start generating menu images
                </p>
                <Button 
                  onClick={handlePurchaseMoreCredits}
                  disabled={processingCheckout}
                  className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 disabled:opacity-50"
                >
                  <ShoppingCart className="mr-2 h-4 w-4" />
                  {processingCheckout ? 'Processing...' : 'Purchase Credits'}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Yearly Summary */}
        {yearlyData.length > 0 && (
          <Card className="mt-8 bg-white/10 backdrop-blur-sm border-white/20">
            <CardHeader>
              <CardTitle className="flex items-center text-white">
                <Calendar className="mr-2 h-5 w-5 text-blue-400" />
                Yearly Summary
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10">
                      <TableHead className="text-white/70">Year</TableHead>
                      <TableHead className="text-right text-white/70">Purchases</TableHead>
                      <TableHead className="text-right text-white/70">Credits Bought</TableHead>
                      <TableHead className="text-right text-white/70">Amount Spent</TableHead>
                      <TableHead className="text-right text-white/70">Credits Used</TableHead>
                      <TableHead className="text-right text-white/70">Usage %</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {yearlyData.map((year) => (
                      <TableRow key={year.year} className="border-white/10">
                        <TableCell className="font-medium text-white">{year.year}</TableCell>
                        <TableCell className="text-right text-white">{year.purchase_count}</TableCell>
                        <TableCell className="text-right text-white">{year.credits_purchased}</TableCell>
                        <TableCell className="text-right text-white">${year.amount_paid.toFixed(2)}</TableCell>
                        <TableCell className="text-right text-white">
                          {year.images_generated} / {year.images_limit}
                        </TableCell>
                        <TableCell className="text-right">
                          <Badge className={year.usage_percentage > 80 ? 'bg-red-500 text-white' : 'bg-yellow-400 text-black hover:bg-yellow-500'}>
                            {year.usage_percentage}%
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AIImagePurchaseHistory;